import { graphql } from 'gatsby';
import React from 'react';
import stripTags from 'striptags';
import { colors } from '../../primitives/colors';
import RowSet from '../../primitives/grid/row-set';
import { StyledButtonAsLink } from '../../primitives/styled-button';
import Typography from '../../primitives/typography';
import paddingDefault from '../../utils/padding-default';
import Container from '../../primitives/grid/container';
import RichTextEditorStyles from '../../utils/rich-text-editor-styles';
import apCaseOnlyTitleTags from '../../utils/ap-case-only-title-tags';
import { breakpoints } from '../../primitives/tokens';
import Divider from '../../primitives/divider';

const TextBanner: React.FC<Queries.TextBannerDataFragment> = (data) => {
    const {
        headline, subheadline, copy, link, settings,
    } = data;

    const hideBackground = !!settings?.hideBackground;
    const darkMode = !!settings?.darkMode;
    const headlineCenter = !!settings?.headlineCenter;
    const textCenter = !!settings?.copyCenter;

    return (
        <div css={[paddingDefault(), {
            backgroundColor: (() => {
                if (darkMode) return colors.shadesBlack;
                if (hideBackground) return colors.shadesWhite;
                return colors.grey50;
            })(),
            color: darkMode ? colors.shadesWhite : undefined,
        }]}
        >
            <Container>
                <div css={{
                    marginBottom: copy ? '32px' : '0',
                    textAlign: textCenter ? 'center' : 'left',
                    [breakpoints.tb]: {
                        textAlign: 'left',
                    },
                }}
                >
                    <RowSet breakpoints={{
                        dt: { between: 16 },
                    }}
                    >
                        {headline && (
                            <Typography
                                as="h2"
                                css={{
                                    textAlign: headlineCenter ? 'center' : 'left',
                                    maxWidth: headlineCenter ? 'unset' : '900px',
                                    [breakpoints.tb]: {
                                        textAlign: 'left',
                                        maxWidth: '80%',
                                    },
                                }}
                                dangerouslySetInnerHTML={{ __html: apCaseOnlyTitleTags(headline) }}
                                fontSize={{
                                    dt: '4xl',
                                    tb: '3xl',
                                    mb: '2xl',
                                }}
                                reducedLetterSpacing
                                fontWeight="regular"
                            />
                        )}
                        {!!subheadline && stripTags(subheadline || '') && (
                            <div
                                css={[RichTextEditorStyles, {
                                    fontSize: '30px',
                                    lineHeight: '36px',
                                    maxWidth: headlineCenter ? 'unset' : '900px',
                                    textAlign: headlineCenter ? 'center' : 'left',
                                    [breakpoints.tb]: {
                                        fontSize: '20px',
                                        lineHeight: 1,
                                    },
                                }]}
                                /* eslint-disable-next-line react/no-danger */
                                dangerouslySetInnerHTML={{ __html: subheadline || '' }}
                            />
                        )}
                    </RowSet>
                </div>
            </Container>
            {(!!copy || (!!link && !!link?.href)) && (
                <>
                    <Divider css={{
                        opacity: darkMode ? 0.1 : 0.6,
                    }}
                    />
                    <Container css={{
                        paddingTop: '48px',
                        [breakpoints.tb]: {
                            paddingTop: '32px',
                        },
                    }}
                    >
                        <RowSet breakpoints={{
                            dt: { between: 16 },
                        }}
                        >
                            {copy && (
                                <div
                                    css={[RichTextEditorStyles, {
                                        fontSize: '20px',
                                        lineHeight: '28px',
                                        maxWidth: textCenter ? '768px' : undefined,
                                        marginRight: textCenter ? 'auto' : undefined,
                                        marginLeft: textCenter ? 'auto' : undefined,
                                        whiteSpace: 'pre-line',
                                        textAlign: textCenter ? 'center' : 'left',
                                        [breakpoints.mb]: {
                                            fontSize: '18px',
                                        },
                                    }]}
                                    /* eslint-disable-next-line react/no-danger */
                                    dangerouslySetInnerHTML={{ __html: copy || '' }}
                                />
                            )}
                            {!!link && !!link?.href && !!link?.title && (
                                <div css={{
                                    display: 'flex',
                                    justifyContent: copy?.includes('iframe') ? 'center' : 'left',
                                }}
                                >
                                    <StyledButtonAsLink to={link.href}>{link.title}</StyledButtonAsLink>
                                </div>
                            )}
                        </RowSet>
                    </Container>
                </>
            )}
        </div>
    );
};

export const query = graphql`
  fragment TextBannerData on CsTextBanner {
    ... on CsTextBanner {
      headline
      subheadline
      copy
      link {
        href
        title
      }
      settings {
        headlineCenter
        copyCenter
        darkMode
        hasBreadcrumbs
        hideBackground
      }
    }
  }
`;

export default TextBanner;
